import { useEffect, useState } from 'preact/hooks';
import { useTranslation } from '../i18n';
import type { Article } from '../api/types';
import { parseToc, publicTags } from '../api/articles';
import { LINKS } from '../brand';

/**
 * Sticky sidebar for the single-article view. Holds:
 * <list type="bullet">
///   <item>The article's TOC (parsed from <c>renderedToc</c> JSON), with
///   the section the reader is currently scrolled to highlighted via
///   IntersectionObserver.</item>
///   <item>Tag chips (when the entity carries any).</item>
/// </list>
///
/// <para><c>lg:self-start</c> is load-bearing: as a grid child the aside
/// defaults to <c>align-self: stretch</c> and fills the full row height
/// (= article body height). A sticky element that already spans its
/// containing block can't visibly stick. <c>self-start</c> shrinks it to
/// content height so the grid row provides scroll room around it.</para>
 */
export function ArticleTocSidebar({ article }: { article: Article }) {
    const t = useTranslation();
    const toc = parseToc(article);
    const tags = publicTags(article);
    const [activeAnchor, setActiveAnchor] = useState<string | null>(null);

    useEffect(() => {
        if (toc.length === 0) return;
        const anchors = toc
            .map((entry) => entry.anchor)
            .filter((a): a is string => typeof a === 'string' && a.length > 0);
        const heads = anchors
            .map((anchor) => document.getElementById(anchor))
            .filter((el): el is HTMLElement => el !== null);
        if (heads.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Pick the entry closest to the top of the viewport that
                // is intersecting. Sorts by boundingClientRect.top so the
                // active section follows the reader's gaze, not the DOM
                // order.
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible.length > 0) {
                    setActiveAnchor(visible[0].target.id);
                }
            },
            { rootMargin: '-80px 0px -60% 0px' },
        );
        heads.forEach((h) => observer.observe(h));
        return () => observer.disconnect();
    }, [article.id]);

    // Note: we no longer early-return when toc + tags are both empty - the
    // Discord CTA below is always worth rendering on the article reader
    // surface (ADR-0020 §D8: the platform's substitute for native
    // comments).

    return (
        <aside class="space-y-8 lg:sticky lg:top-20 lg:self-start">
            {toc.length > 0 && (
                <section>
                    <h2 class="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-accent">
                        {t('articles.tocTitle')}
                    </h2>
                    <ol class="space-y-1.5">
                        {toc.map((entry) => (
                            <li
                                key={entry.anchor ?? entry.text}
                                class={`text-[13px] leading-[1.4] ${entry.level >= 3 ? 'pl-3' : ''}`}
                            >
                                {entry.anchor ? (
                                    <a
                                        href={`#${entry.anchor}`}
                                        class={`block py-0.5 transition-colors ${
                                            activeAnchor === entry.anchor
                                                ? '-ml-2 border-l-2 border-accent pl-2 text-accent'
                                                : 'text-fg-muted hover:text-fg-primary'
                                        }`}
                                    >
                                        {entry.text}
                                    </a>
                                ) : (
                                    <span class="block py-0.5 text-fg-muted">{entry.text}</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {tags.length > 0 && (
                <section>
                    <h2 class="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                        {t('articles.tagsTitle')}
                    </h2>
                    <div class="flex flex-wrap gap-1.5">
                        {tags.map((tag) => (
                            <span
                                key={tag}
                                class="inline-flex items-center rounded-full border border-rule px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted"
                            >
                                #{tag}
                            </span>
                        ))}
                    </div>
                </section>
            )}

            {/* ADR-0020 §D8: Discord is the platform's substitute for
                native comments. Surface it on every article reader so
                discussion stays in one place instead of fragmenting
                across the catalog. */}
            <section>
                <a
                    href={LINKS.discord.invite}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex w-full items-center justify-center gap-2 rounded-full border border-rule bg-surface-raised px-4 py-2 font-sans text-[13px] text-fg-primary transition-colors hover:border-accent-soft hover:text-accent"
                >
                    <span>{t('articles.discussOnDiscord')}</span>
                    <span aria-hidden="true">→</span>
                </a>
            </section>
        </aside>
    );
}
